import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle2 } from 'lucide-react';

// Formsubmit.co — envío directo por email, sin backend cross-origin (iPad-safe)
const FORM_ENDPOINT = 'https://formsubmit.co/ajax/info@tecnoabogado.com';

const SERVICE_LABELS = {
  implementacion: 'Implementación 1 a 1',
  continuidad: 'Continuidad (Membresía)',
  ambos: 'Ambos servicios',
  consulta: 'Solo consulta',
};

const Contacto = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: 'implementacion',
    message: ''
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Honeypot anti-spam
    if (e.target._honey && e.target._honey.value) {
      return;
    }

    try {
      const payload = {
        Nombre: formData.name,
        Email: formData.email,
        Teléfono: formData.phone || '—',
        Servicio: SERVICE_LABELS[formData.service_type] || formData.service_type,
        Mensaje: formData.message || '—',
        _subject: `Nueva solicitud Tecnoabogado — ${SERVICE_LABELS[formData.service_type] || 'contacto'}`,
        _template: 'table',
        _captcha: 'false',
      };

      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && (data.success === 'true' || data.success === true)) {
        setStatus({
          type: 'success',
          message: '¡Gracias! Hemos recibido tu solicitud. Nos pondremos en contacto contigo en las próximas 24 horas.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          service_type: 'implementacion',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Respuesta no válida');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({
        type: 'error',
        message: 'Hubo un error al enviar tu solicitud. Por favor, escríbenos directamente a info@tecnoabogado.com.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="bg-reset-black py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left Column - Info */}
          <div>
            <span className="text-reset-gold uppercase tracking-widest text-sm mb-4 block">
              ¿Listo para transformar tu despacho?
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-white mb-6">
              Solicita tu <span className="italic text-reset-gold">acceso prioritario</span>
            </h2>
            <p className="text-xl text-gray-400 leading-relaxed mb-8">
              Completa el formulario y recibirás una respuesta personalizada en menos de 24 horas. Evaluaremos tu caso y te propondremos el mejor plan de acción.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-reset-gold/10 p-3 rounded">
                  <Mail className="text-reset-gold" size={24} />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Email</p>
                  <a href="mailto:info@tecnoabogado.com" className="text-gray-400 hover:text-reset-gold transition-colors">
                    info@tecnoabogado.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-reset-gold/10 p-3 rounded">
                  <Phone className="text-reset-gold" size={24} />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Teléfono / WhatsApp</p>
                  <a href="tel:+34954564602" className="text-gray-400 hover:text-reset-gold transition-colors">
                    +34 954 564 602
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-reset-dark border border-reset-gold/30 p-6">
              <p className="text-white font-semibold mb-2">
                🔒 Confidencialidad garantizada
              </p>
              <p className="text-gray-400 text-sm">
                Toda la información compartida está protegida por acuerdo de confidencialidad y cumple con el RGPD.
              </p>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-reset-dark border border-reset-gold/20 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot anti-spam */}
              <input type="text" name="_honey" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
              <div>
                <label htmlFor="name" className="block text-white font-semibold mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-reset-black border border-reset-gold/30 text-white px-4 py-3 focus:outline-none focus:border-reset-gold transition-colors"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-white font-semibold mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-reset-black border border-reset-gold/30 text-white px-4 py-3 focus:outline-none focus:border-reset-gold transition-colors"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-white font-semibold mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-reset-black border border-reset-gold/30 text-white px-4 py-3 focus:outline-none focus:border-reset-gold transition-colors"
                  placeholder="+34 600 000 000"
                />
              </div>

              <div>
                <label htmlFor="service_type" className="block text-white font-semibold mb-2">
                  Servicio de interés *
                </label>
                <select
                  id="service_type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  required
                  className="w-full bg-reset-black border border-reset-gold/30 text-white px-4 py-3 focus:outline-none focus:border-reset-gold transition-colors"
                >
                  <option value="implementacion">Implementación 1 a 1</option>
                  <option value="continuidad">Continuidad (Membresía)</option>
                  <option value="ambos">Ambos servicios</option>
                  <option value="consulta">Solo consulta</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-white font-semibold mb-2">
                  Mensaje
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full bg-reset-black border border-reset-gold/30 text-white px-4 py-3 focus:outline-none focus:border-reset-gold transition-colors resize-none"
                  placeholder="Cuéntanos brevemente sobre tu despacho y tus necesidades..."
                ></textarea>
              </div>

              {status.message && (
                <div className={`p-4 border ${
                  status.type === 'success' 
                    ? 'bg-green-900/20 border-green-500/50 text-green-300' 
                    : 'bg-red-900/20 border-red-500/50 text-red-300'
                } flex items-start gap-3`}>
                  {status.type === 'success' && <CheckCircle2 size={20} className="shrink-0 mt-0.5" />}
                  <p className="text-sm">{status.message}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-form-submit"
                className="w-full bg-reset-gold hover:bg-reset-gold-light text-black py-4 text-lg font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar solicitud'}
                <Send size={20} />
              </button>

              <p className="text-gray-500 text-xs text-center">
                Al enviar este formulario, aceptas nuestra política de privacidad y el tratamiento de tus datos según el RGPD.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contacto;